import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactCrop, {
    centerCrop,
    convertToPixelCrop,
    Crop,
    makeAspectCrop,
    PixelCrop,
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import { t } from '../../../common/i18n.js'
import type { Language } from '../../../common/types/i18n.js'
import type { ModalAction } from '../modal/action-modal/action-modal.js'
import '../modal/cropper-modal/crop-modal.scss'

export type CropperResult = {
    blob: Blob
    objectUrl: string
    width: number
    height: number
    revokeObjectUrl: () => void
}

export type CropperProps = {
    src?: string
    lang: Language
    onCropChange?: (crop: Crop) => void
    onComplete: (result: CropperResult) => void
    onCancel?: () => void
    outputType?: 'image/png' | 'image/jpeg' | 'image/webp'
    jpegQuality?: number
    confirmText?: string
    cancelText?: string
    onActionsChange?: (actions: ModalAction[]) => void
    showGrid?: boolean
}

const OUTPUT_SIZE = 91
const ASPECT = 1

function clampQuality(q?: number): number | undefined {
    if (typeof q !== 'number' || Number.isNaN(q)) return undefined
    return Math.min(1, Math.max(0, q))
}

function createCenteredSquareCrop(mediaWidth: number, mediaHeight: number): Crop {
    const smaller = Math.min(mediaWidth, mediaHeight)
    const cropWidthPx = Math.floor(smaller)

    const crop = makeAspectCrop({ unit: 'px', width: cropWidthPx }, ASPECT, mediaWidth, mediaHeight)

    const centered = centerCrop(crop, mediaWidth, mediaHeight)

    return {
        unit: '%',
        x: (centered.x / mediaWidth) * 100,
        y: (centered.y / mediaHeight) * 100,
        width: (centered.width / mediaWidth) * 100,
        height: (centered.height / mediaHeight) * 100,
    }
}

async function cropToFixedSizeBlob(options: {
    image: HTMLImageElement
    pixelCrop: PixelCrop
    outputType: 'image/png' | 'image/jpeg' | 'image/webp'
    jpegQuality?: number
    outputSize: number
}): Promise<Blob> {
    const { image, pixelCrop, outputType, jpegQuality, outputSize } = options

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const sx = Math.round(pixelCrop.x * scaleX)
    const sy = Math.round(pixelCrop.y * scaleY)
    const sWidth = Math.round(pixelCrop.width * scaleX)
    const sHeight = Math.round(pixelCrop.height * scaleY)

    const dpr = window.devicePixelRatio || 1

    const canvas = document.createElement('canvas')
    canvas.width = outputSize * dpr
    canvas.height = outputSize * dpr

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas context not available')

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Clear canvas
    ctx.clearRect(0, 0, outputSize, outputSize)

    // Create circular clipping path
    ctx.save()
    ctx.beginPath()
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2)
    ctx.closePath()
    ctx.clip()

    // Draw image inside circle
    ctx.drawImage(image, sx, sy, sWidth, sHeight, 0, 0, outputSize, outputSize)

    // Restore context
    ctx.restore()

    const quality = outputType === 'image/jpeg' ? clampQuality(jpegQuality) : undefined

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Canvas toBlob failed'))
                    return
                }
                resolve(blob)
            },
            outputType,
            quality,
        )
    })
}

/* ======================================================
   Component
   ====================================================== */

export const ImageCropper: React.FC<CropperProps> = ({
    src,
    lang,
    onComplete,
    onCancel,
    outputType = 'image/png',
    jpegQuality = 1,
    confirmText,
    cancelText,
    onActionsChange,
    onCropChange,
    showGrid = true,
}) => {
    const resolvedConfirmText = confirmText ?? t.cropper.save(lang)
    const resolvedCancelText = cancelText ?? t.cropper.cancel(lang)
    const imgRef = useRef<HTMLImageElement | null>(null)

    const [crop, setCrop] = useState<Crop>()
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isWorking, setIsWorking] = useState(false)

    const resolvedSrc = useMemo(() => src ?? null, [src])

    /* ----------------------------------------------
       Stable initialization guard
       ---------------------------------------------- */
    const hasInitializedCropRef = useRef(false)

    useEffect(() => {
        hasInitializedCropRef.current = false
    }, [resolvedSrc])

    /* ----------------------------------------------
       Image load handler
       ---------------------------------------------- */
    const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        if (hasInitializedCropRef.current) return

        const img = e.currentTarget
        const initial = createCenteredSquareCrop(img.width, img.height)
        setCrop(initial)
        const px = convertToPixelCrop(initial, img.width, img.height)

        setCompletedCrop(px)

        setError(null)
        hasInitializedCropRef.current = true
    }, [])

    /* ----------------------------------------------
       Confirm handler
       ---------------------------------------------- */
    const handleConfirm = useCallback(async () => {
        if (!imgRef.current || !completedCrop) return

        setIsWorking(true)
        setError(null)

        try {
            const blob = await cropToFixedSizeBlob({
                image: imgRef.current,
                pixelCrop: completedCrop,
                outputType,
                jpegQuality,
                outputSize: OUTPUT_SIZE,
            })

            const objectUrl = URL.createObjectURL(blob)

            onComplete({
                blob,
                objectUrl,
                width: OUTPUT_SIZE,
                height: OUTPUT_SIZE,
                revokeObjectUrl: () => URL.revokeObjectURL(objectUrl),
            })
        } catch (_err) {
            setError(t.cropper.failedToCrop(lang))
        } finally {
            setIsWorking(false)
        }
    }, [completedCrop, onComplete, outputType, jpegQuality])

    const actions = useMemo<ModalAction[] | null>(() => {
        if (!resolvedSrc || !completedCrop) return null

        const cancelButton: ModalAction = {
            label: resolvedCancelText,
            variant: 'alert',
            onClick: () => {
                return onCancel ? onCancel() : undefined
            },
        }
        const primaryAction: ModalAction = {
            label: isWorking ? t.cropper.processing(lang) : resolvedConfirmText,
            variant: 'primary',
            disabled: isWorking,
            onClick: () => {
                void handleConfirm()
            },
        }

        return [cancelButton, primaryAction]
    }, [
        resolvedSrc,
        completedCrop,
        resolvedCancelText,
        resolvedConfirmText,
        isWorking,
        onCancel,
        handleConfirm,
    ])

    const prevActionsRef = useRef<ModalAction[] | null>(null)

    useEffect(() => {
        const currentActions = actions ?? []
        const prevActions = prevActionsRef.current

        if (
            prevActions?.length === currentActions.length &&
            JSON.stringify(prevActions) === JSON.stringify(currentActions)
        ) {
            return
        }

        prevActionsRef.current = currentActions
        onActionsChange?.(currentActions)
    }, [actions])

    if (!resolvedSrc) {
        return <div className="cropper__empty">{t.common.noImageSelected(lang)}</div>
    }

    return (
        <div className="cropper" aria-busy={isWorking}>
            <div className="cropper__body">
                <div className="cropper__stage">
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => {
                            setCrop(percentCrop)
                            onCropChange?.(percentCrop)
                        }}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={ASPECT}
                        circularCrop
                        keepSelection
                        ruleOfThirds={showGrid}
                    >
                        <img
                            ref={imgRef}
                            src={resolvedSrc}
                            alt={t.common.cropSourceAlt(lang)}
                            draggable={false}
                            onLoad={handleImageLoad}
                        />
                    </ReactCrop>
                </div>

                {error && <div className="cropper__error">{error}</div>}
            </div>
        </div>
    )
}
